import type { MetricSummary } from '@/types'

export interface MetricDisplayItem {
  label: string
  value: string
  key: string
}

const userMetricLabels: Record<string, string> = {
  inFlight: 'In Flight',
  completed: 'Completed',
  failed: 'Failed',
  allocated: 'Allocated',
  remaining: 'Remaining',
  available: 'Available',
  fileCount: 'File Count',
  validationCount: 'Validation Count',
  validationsInFlight: 'Validations In Flight',
}

const tenantMetricLabels: Record<string, string> = {
  inFlight: 'In Flight',
  completed: 'Completed',
  failed: 'Failed',
  allocated: 'Allocated',
  remaining: 'Remaining',
  available: 'Available',
}

const subscriptionMetricLabels: Record<string, string> = {
  status: 'Status',
  planCode: 'Plan',
  type: 'Type',
  startDateTimeUtc: 'Period Start',
  endDateTimeUtc: 'Period End',
  maxExecutions: 'Max Executions',
  maxProfiles: 'Max Profiles',
  userAllocation: 'Seat Count',
  billingInterval: 'Billing Interval',
}

/** Subscription fields to exclude from display */
const subscriptionExcludedFields = new Set(['id'])

/** Fields that display "Unlimited" when the value is null or "0" and unlimited is true */
const unlimitableFields = new Set(['allocated', 'remaining', 'available'])

/**
 * Formats a metric value for display.
 * - For unlimitable fields (allocated, remaining, available): shows "Unlimited"
 *   when the value is null (API signals unlimited with null).
 * - For all other fields: falls back to "0" when null/undefined.
 */
function formatMetricValue(key: string, value: string | null | undefined): string {
  if (unlimitableFields.has(key) && (value === null || value === undefined)) {
    return 'Unlimited'
  }
  return value ?? '0'
}

/**
 * Maps the user-level metrics from a MetricSummary to an array of MetricDisplayItems.
 * Excludes the `id` field since it is not a displayable metric.
 */
export function mapUserMetrics(user: MetricSummary['user']): MetricDisplayItem[] {
  return Object.entries(userMetricLabels).map(([key, label]) => ({
    label,
    value: formatMetricValue(key, user[key as keyof typeof user] as string | null | undefined),
    key,
  }))
}

/**
 * Maps the tenant-level metrics from a MetricSummary to an array of MetricDisplayItems.
 * Excludes the `id` field since it is not a displayable metric.
 */
export function mapTenantMetrics(tenant: MetricSummary['tenant']): MetricDisplayItem[] {
  return Object.entries(tenantMetricLabels).map(([key, label]) => ({
    label,
    value: formatMetricValue(key, tenant[key as keyof typeof tenant] as string | null | undefined),
    key,
  }))
}

/**
 * Converts a camelCase key to a Title Case label as a fallback
 * when no explicit label mapping exists.
 */
function camelToTitleCase(str: string): string {
  return str
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim()
}

/**
 * Formats a subscription value for display.
 * Handles null/undefined/"0" for unlimited-style fields.
 */
function formatSubscriptionValue(key: string, value: unknown): string {
  if (value === null || value === undefined) {
    if (key === 'maxExecutions' || key === 'maxProfiles') return 'Unlimited'
    return ''
  }
  if (key === 'maxExecutions' && (value === 0 || value === '0')) return 'Unlimited'
  return String(value)
}

/**
 * Maps the subscription metrics from a MetricSummary to an array of MetricDisplayItems.
 * Uses explicit label mappings where available, falls back to camelCase-to-Title-Case conversion.
 * Excludes internal fields like `id`.
 */
export function mapSubscriptionMetrics(subscription: Record<string, any>): MetricDisplayItem[] {
  // First render items with explicit labels in defined order
  const items: MetricDisplayItem[] = []
  const rendered = new Set<string>()

  for (const [key, label] of Object.entries(subscriptionMetricLabels)) {
    if (key in subscription) {
      items.push({
        label,
        value: formatSubscriptionValue(key, subscription[key]),
        key,
      })
      rendered.add(key)
    }
  }

  // Then render any remaining fields not in the label map or exclusion list
  for (const [key, value] of Object.entries(subscription)) {
    if (rendered.has(key) || subscriptionExcludedFields.has(key)) continue
    items.push({
      label: camelToTitleCase(key),
      value: formatSubscriptionValue(key, value),
      key,
    })
  }

  return items
}
